import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import Table from "components/DesignSystem/Table/V2/Table";
import { DISPLAY_DATE } from "constants/date";
import {
  ANNUALLY,
  MONTHLY,
  NONE,
  QUARTERLY,
  SEMI_ANNUALLY,
} from "constants/salesTaxRegistrationStatusType";
import dayjs from "dayjs";
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
} from "react-table-8.10.7";
import { FilingSlider } from "./FilingSlider";
import { FilingRowAction } from "./FilingRowAction";
import { useModal } from "hooks/useModal";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { FilingSelectionControls } from "./FilingSelectionControls";
import { TaxFiling, useGetAllFilingsQuery } from "store/apis/filing";
import { CaretDown } from "components/icons/CaretDown";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { Exclamation } from "components/icons/Exclamation";
import { SmallArrowDown } from "components/icons/SmallArrowDown";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { isOverdue, statusMap, TabType } from "./Filing";
import {
  NOT_STARTED,
  IN_CART,
  FILED,
  PROCESSING,
  SUBSCRIBED,
} from "constants/filing";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { getBillingCartUrl } from "util/getBillingCartUrl";

const getToolTipTextBasedOnStatus = (status: string, isOverdue: Boolean) => {
  switch (status) {
    case IN_CART:
      return (
        <p className="t-m-0">
          Added to cart — complete payment to start this filing.
        </p>
      );
    case SUBSCRIBED:
      return (
        <p className="t-m-0">
          Covered by an active subscription — filing will start automatically
          once the subscription is billed.
        </p>
      );
    case PROCESSING:
      return isOverdue ? (
        <p className="t-m-0">
          Your filing is being processed but is past the due date.
        </p>
      ) : (
        <p className="t-m-0">Preparing your filing based on confirmed sales.</p>
      );
    case FILED:
      return (
        <p className="t-m-0">
          Sales tax return successfully filed for this period.
        </p>
      );
    case NOT_STARTED:
      return isOverdue ? (
        <p className="t-m-0">
          Past due date. <br /> Urgent action needed.
        </p>
      ) : (
        <p className="t-m-0">
          Filing period ended. <br /> Verify all sales and start filing.
        </p>
      );
    default:
      return "";
  }
};

export const StatusBadge = ({
  children,
  status,
}: {
  children: ReactNode;
  status: string;
}) => {
  return (
    <div
      className={classNames(
        "t-flex t-items-center t-gap-2 t-max-w-fit t-text-text-60 t-py-1 t-px-2 t-rounded-full",
        {
          "t-bg-blue-10": status === IN_CART,
          "t-bg-purple-0": status === SUBSCRIBED,
          "t-bg-orange-10": status === PROCESSING,
          "t-bg-green-20": status === FILED,
          "!t-bg-neutral-0 !t-text-neutral-70": status === NOT_STARTED,
        }
      )}
    >
      <span className="t-min-w-fit t-text-body-sm  t-whitespace-nowrap">
        {children}
      </span>
    </div>
  );
};

const createColumn = createColumnHelper<TaxFiling>();

// Eligible for "mark as filed" (self-report) for any filing that isn't
// already FILED — only a completed filing is locked from this action.
const isEligibleForSelfReport = (filing: TaxFiling): boolean => {
  return filing.status !== FILED;
};

const paymentScheduleMap = {
  [SEMI_ANNUALLY]: "Semi-Annually",
  [QUARTERLY]: "Quarterly",
  [ANNUALLY]: "Annually",
  [MONTHLY]: "Monthly",
  [NONE]: "",
};

const getFilingYear = (filing: TaxFiling) =>
  dayjs(filing.filing_period_start_date).year();

export const FilingAccordionTable = ({
  tab,
  handleAddClick,
  searchTerm,
  selectedSeasons,
}: {
  tab: TabType;
  handleAddClick?: () => void;
  searchTerm?: string | null;
  selectedSeasons?: string[];
}) => {
  const fillingSlider = useModal();
  const query = useQuery();
  const filing_id = query.get("filing_id");
  const taxYearParam = query.get("tax_year");

  const [selectedFilling, setSelectedFilling] = useState<string>("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const entityId = useCurrentEntityId();
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();

  const seasonsFilter = selectedSeasons?.length
    ? selectedSeasons.join(",")
    : "ALL";

  const { data, isLoading, isSuccess } = useGetAllFilingsQuery({
    entityId,
    status: tab,
    seasons: seasonsFilter,
    searchTerm,
  });

  const filingsByYear = useMemo(() => {
    const grouped = new Map<number, TaxFiling[]>();
    (data || []).forEach((filing) => {
      const year = getFilingYear(filing);
      grouped.set(year, [...(grouped.get(year) || []), filing]);
    });
    return Array.from(grouped.entries()).sort(([a], [b]) => b - a);
  }, [data]);

  const defaultExpandedYear = filingsByYear[0]?.[0];

  const [manuallyExpandedYears, setManuallyExpandedYears] =
    useState<Set<number> | null>(null);

  const hasTaxYearParam = Boolean(taxYearParam) && tab === "action_required";

  const getExpandedYears = (): Set<number> => {
    if (hasTaxYearParam) {
      return new Set([Number(taxYearParam)]);
    }
    if (manuallyExpandedYears) {
      return manuallyExpandedYears;
    }
    return new Set(defaultExpandedYear ? [defaultExpandedYear] : []);
  };

  const expandedYears = getExpandedYears();

  const toggleYear = (year: number) => {
    const next = new Set(expandedYears);
    if (next.has(year)) {
      next.delete(year);
    } else {
      next.add(year);
    }
    setManuallyExpandedYears(next);
  };

  const sortedFilings = useMemo(
    () => filingsByYear.flatMap(([, filings]) => filings),
    [filingsByYear]
  );

  const handleRowClick = (row: TaxFiling) => {
    setSelectedFilling(row.uuid);
    fillingSlider.open();
  };

  const handleViewCart = useCallback(() => {
    const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
    window.open(cartUrl, "_blank");
  }, [isAdmin, groupId, entityId]);

  const handleViewStatus = useCallback((taskId: string) => {
    window.open(`/tax/filings/${taskId}`, "_blank");
  }, []);

  useMemo(() => {
    if (filing_id) {
      setSelectedFilling(filing_id);
      fillingSlider.open();
    }
  }, [filing_id]);

  const columns = useMemo(
    () => [
      createColumn.display({
        id: "select",
        header: ({ table }) => <SelectionHeaderCell table={table} />,
        size: 5,
        cell: ({ row }) => (
          <SelectionCell row={row} onClick={(e) => e.stopPropagation()} />
        ),
      }),
      createColumn.accessor("state", {
        header: () => (
          <p className="t-m-0 t-pl-3 t-flex t-items-center t-gap-1">
            State <SmallArrowDown />
          </p>
        ),
        size: 25,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <div className="t-pl-3">
              <p className="t-text-body-sm t-leading-0 t-m-0 t-font-medium t-text-text-100">
                {info.getValue()}
              </p>
              <p className="t-text-body-sm t-m-0 t-flex t-items-center t-leading-0 t-text-text-30">
                {dayjs(rowData.filing_period_start_date).format("MMM")} -{" "}
                {dayjs(rowData.filing_period_end_date).format("MMM YYYY")}{" "}
                <span className="t-inline-block t-bg-neutral-20 t-h-2 t-rounded-md t-w-[1px] t-mx-1" />
                {
                  paymentScheduleMap[
                    rowData?.payment_schedule as keyof typeof paymentScheduleMap
                  ]
                }
              </p>
            </div>
          );
        },
      }),
      createColumn.accessor("filing_due_date", {
        header: "Deadline",
        size: 16,
        cell: (info) => {
          const isRowOverdue =
            isOverdue(info.getValue()) && info.row.original.status !== FILED;
          return (
            <div className="t-flex t-items-center t-gap-1.5 t-py-1">
              <CalendarIcon
                className={isRowOverdue ? "t-text-red" : "t-text-text-30"}
              />
              <span
                className={classNames("t-text-body-sm", {
                  "t-text-red": isRowOverdue,
                  "t-text-text-60": !isRowOverdue,
                })}
              >
                {dayjs(info.getValue()).format(DISPLAY_DATE)}
              </span>
              {isRowOverdue && (
                <span className="t-flex t-items-center t-text-red">
                  <Exclamation width={12} height={12} />
                </span>
              )}
            </div>
          );
        },
      }),
      createColumn.accessor("total_sales", {
        header: () => <p className="t-text-end t-m-0">Sales</p>,
        size: 12,
        cell: (info) => (
          <p className="t-text-text-60 t-m-0 t-text-end ">
            <AmountSuperScript amount={info.getValue()} />
          </p>
        ),
      }),
      createColumn.accessor("total_tax_collected", {
        header: () => <p className="t-text-end t-m-0 t-mr-8">Tax collected</p>,
        size: 15,
        cell: (info) => (
          <p className="t-text-text-60 t-m-0 t-text-end t-mr-8">
            <AmountSuperScript amount={info.getValue()} />
          </p>
        ),
      }),
      createColumn.accessor("status", {
        header: "Status",
        size: 20,
        cell: (info) => {
          const status = info.getValue();

          return (
            <span>
              <ConditionalToolTip
                condition={status}
                text={getToolTipTextBasedOnStatus(
                  status,
                  isOverdue(info.row.original.filing_due_date)
                )}
              >
                <div className="t-w-fit">
                  <StatusBadge status={status}>
                    {statusMap[status as keyof typeof statusMap]}
                  </StatusBadge>
                </div>
              </ConditionalToolTip>
            </span>
          );
        },
      }),
      createColumn.display({
        header: "Action",
        size: 18,
        cell: (row) => (
          <FilingRowAction
            filing={row.row.original}
            onViewStatus={handleViewStatus}
            onViewCart={handleViewCart}
          />
        ),
      }),
    ],
    [handleViewCart, handleViewStatus]
  );

  const table = useReactTable({
    data: sortedFilings,
    getCoreRowModel: getCoreRowModel(),
    columns: columns,
    defaultColumn: {
      minSize: 6,
    },
    getRowId: (row) => row.uuid,
    enableRowSelection: (row) => isEligibleForSelfReport(row.original),
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
  });

  const columnCount = columns.length;

  const isFirstRowOfYear = (filingId: string) => {
    for (const [, filings] of filingsByYear) {
      if (filings[0]?.uuid === filingId) {
        return true;
      }
    }
    return false;
  };

  const renderYearHeaderRow = (year: number) => {
    const isYearExpanded = expandedYears.has(year);

    return (
      <Table.Row
        key={`year-${year}`}
        className="t-cursor-pointer t-bg-surface-grey hover:!t-bg-surface-grey"
        onRowClick={() => toggleYear(year)}
      >
        <Table.Cell
          colSpan={columnCount}
          className="!t-py-1.5 t-border-0 t-border-solid t-border-t-4 t-border-white !t-px-3"
        >
          <div className="t-text-subtext-sm t-text-text-60">
            Tax year {year}
            <CaretDown
              className={classNames(
                "t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out t-ml-2",
                {
                  "t-rotate-180": isYearExpanded,
                  "t-rotate-0": !isYearExpanded,
                }
              )}
            />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={!data?.length && !isLoading}
    >
      <Async.Success>
        <div className="t-h-full t-w-full">
          <TableUI
            table={table}
            isHeaderCellUpperCase={false}
            onRowClick={(row) => handleRowClick(row.original)}
          >
            {({ row }) => {
              const year = getFilingYear(row.original);
              const isFirstOfYear = isFirstRowOfYear(row.original.uuid);
              const isYearExpanded = expandedYears.has(year);

              return (
                <>
                  {isFirstOfYear && renderYearHeaderRow(year)}
                  {isYearExpanded && (
                    <Table.Row
                      onRowClick={() => handleRowClick(row.original)}
                      isRowSelected={row.getIsSelected()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}%` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )}
                </>
              );
            }}
          </TableUI>

          <FilingSlider
            onClose={fillingSlider.close}
            isOpen={fillingSlider.isOpen}
            selectedFilling={selectedFilling}
            onAddTransaction={handleAddClick}
          />
        </div>
        <FilingSelectionControls table={table} entityId={entityId} />
      </Async.Success>
    </Async.Root>
  );
};
